(function () {
  const STORAGE_KEY = "discountPreviewCode";
  const STORAGE_TIME_KEY = "discountPreviewTimestamp";
  const CONFIG_KEY = "discountPreviewConfigKey";

  const config = window.discountPreview;
  if (!config?.enabled) {
    return;
  }

  const activeCode = (config.code || "").trim().toUpperCase();
  if (!activeCode) {
    return;
  }

  let resolvedCode = null;
  let applyQueued = false;

  function clearStoredCode() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TIME_KEY);
  }

  function syncConfigFingerprint() {
    const configKey = config.configKey;
    if (!configKey) {
      return;
    }

    const storedKey = localStorage.getItem(CONFIG_KEY);
    if (storedKey !== configKey) {
      clearStoredCode();
      localStorage.setItem(CONFIG_KEY, configKey);
    }
  }

  function isExpired(timestamp) {
    const hours = Number(config.expiryHours) || 24;
    return Date.now() - timestamp > hours * 60 * 60 * 1000;
  }

  function getCodeFromUrl() {
    const match = window.location.pathname.match(/^\/discount\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).trim().toUpperCase() : null;
  }

  function saveCode(code) {
    localStorage.setItem(STORAGE_KEY, code.toUpperCase());
    localStorage.setItem(STORAGE_TIME_KEY, String(Date.now()));
  }

  function getStoredCode() {
    const code = localStorage.getItem(STORAGE_KEY);
    const timestamp = parseInt(localStorage.getItem(STORAGE_TIME_KEY), 10);

    if (!code || !timestamp || isExpired(timestamp)) {
      clearStoredCode();
      return null;
    }

    return code.toUpperCase();
  }

  function isActiveCode(code) {
    return Boolean(code) && code.toUpperCase() === activeCode;
  }

  async function getCodeFromCart() {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) {
        return null;
      }

      const cart = await response.json();
      const discountCodes = cart.discount_codes || [];

      for (const discount of discountCodes) {
        if (discount.code && isActiveCode(discount.code)) {
          return discount.code.toUpperCase();
        }
      }

      const applications = cart.cart_level_discount_applications || [];

      for (const application of applications) {
        const title = application.title || application.key;
        if (title && isActiveCode(title)) {
          return title.toUpperCase();
        }
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  async function resolveActiveDiscountCode() {
    const urlCode = getCodeFromUrl();
    if (urlCode && isActiveCode(urlCode)) {
      saveCode(urlCode);
      return urlCode;
    }

    const storedCode = getStoredCode();
    if (storedCode && isActiveCode(storedCode)) {
      return storedCode;
    }

    const cartCode = await getCodeFromCart();
    if (cartCode) {
      saveCode(cartCode);
      return cartCode;
    }

    return null;
  }

  function formatMoney(cents) {
    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents, config.moneyFormat);
    }

    return (cents / 100).toFixed(2);
  }

  function calculateDiscountedPrice(priceCents) {
    if (config.type !== "percentage") {
      return null;
    }

    const value = Number(config.value);
    if (!value || value <= 0 || value >= 100) {
      return null;
    }

    return Math.round(priceCents - (priceCents * value) / 100);
  }

  function isEligible(element) {
    const scope = config.scope || "all";

    if (scope === "all") {
      return true;
    }

    const productId = Number(element.dataset.productId);
    const collectionHandles = (element.dataset.collectionHandles || "")
      .split(",")
      .map((handle) => handle.trim())
      .filter(Boolean);

    if (scope === "products") {
      return (config.productIds || []).some((id) => Number(id) === productId);
    }

    if (scope === "collections") {
      return (config.collectionHandles || []).some((handle) => collectionHandles.includes(handle));
    }

    return false;
  }

  function getTaxSuffixMarkup(element) {
    const suffix = element.querySelector(".price-item .sup");
    return suffix ? suffix.outerHTML : "";
  }

  function restoreOriginal(element) {
    const container = element.querySelector(".price-container");
    const originalHtml = element.dataset.discountOriginalHtml;

    if (container && originalHtml) {
      container.innerHTML = originalHtml;
      delete element.dataset.discountOriginalHtml;
    }

    delete element.dataset.discountApplied;
    element.classList.remove("discount-preview-active");
  }

  function buildSaleMarkup(originalCents, discountedCents, taxSuffixMarkup) {
    return `
      <div class="price-sale">
        <span class="visually-hidden">Regular price</span>
        <span>
          <s class="price-item price-item-regular">${formatMoney(originalCents)}</s>
        </span>
        <span class="visually-hidden">Sale price</span>
        <span class="price-item price-item-sale price-item-last">
          ${formatMoney(discountedCents)}${taxSuffixMarkup}
        </span>
      </div>
    `;
  }

  function applyDiscountToElement(element) {
    if (!resolvedCode) {
      restoreOriginal(element);
      return;
    }

    if (!isEligible(element)) {
      restoreOriginal(element);
      return;
    }

    const priceCents = parseInt(element.dataset.price, 10);
    const compareAtCents = parseInt(element.dataset.compareAtPrice, 10) || 0;
    const discountedCents = calculateDiscountedPrice(priceCents);

    if (!priceCents || !discountedCents || discountedCents >= priceCents) {
      restoreOriginal(element);
      return;
    }

    const container = element.querySelector(".price-container");
    if (!container) {
      return;
    }

    if (!element.dataset.discountOriginalHtml) {
      element.dataset.discountOriginalHtml = container.innerHTML;
    }

    const taxSuffixMarkup = getTaxSuffixMarkup(element);
    const strikeCents = compareAtCents > priceCents ? compareAtCents : priceCents;
    const saleMarkup = buildSaleMarkup(strikeCents, discountedCents, taxSuffixMarkup);

    element.classList.add("price-on-sale", "discount-preview-active");
    element.dataset.discountApplied = "true";

    const existingSale = container.querySelector(".price-sale");
    if (existingSale) {
      existingSale.outerHTML = saleMarkup;
    } else {
      container.insertAdjacentHTML("beforeend", saleMarkup);
    }
  }

  function updateBanner() {
    const banner = document.getElementById("discount-preview-banner");
    if (!banner) {
      return;
    }

    const textNode = banner.querySelector(".discount-preview-banner-text");
    if (!resolvedCode) {
      banner.hidden = true;
      banner.classList.add("hidden");
      return;
    }

    const template = config.bannerText || "Discount code {{ code }} applied";
    const message = template.replace(/\{\{\s*code\s*\}\}/gi, resolvedCode);

    if (textNode) {
      textNode.textContent = message;
    }

    banner.hidden = false;
    banner.classList.remove("hidden");
  }

  function applyDiscountPrices() {
    document.querySelectorAll("[data-discount-price]").forEach((element) => {
      applyDiscountToElement(element);
    });

    updateBanner();
  }

  function queueApplyDiscountPrices() {
    if (applyQueued) {
      return;
    }

    applyQueued = true;
    window.requestAnimationFrame(() => {
      applyQueued = false;
      applyDiscountPrices();
    });
  }

  async function refreshDiscountState() {
    syncConfigFingerprint();
    resolvedCode = await resolveActiveDiscountCode();
    applyDiscountPrices();
  }

  function setupListeners() {
    document.addEventListener("product:variant-change", queueApplyDiscountPrices);

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.productVariantChange, queueApplyDiscountPrices);
    }

    document.addEventListener("collection:product-filters", () => {
      window.setTimeout(queueApplyDiscountPrices, 0);
    });

    const productGrid = document.getElementById("ProductGridContainer");
    if (productGrid) {
      const observer = new MutationObserver(queueApplyDiscountPrices);
      observer.observe(productGrid, { childList: true, subtree: true });
    }
  }

  function init() {
    setupListeners();
    refreshDiscountState();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) {
      return;
    }

    refreshDiscountState();
  });
})();
